import { useState, useEffect, useCallback } from "react";
import { Check, ChevronsUpDown, Plus, User, Loader2, X } from "lucide-react";
import { cn } from "../lib/utils";
import { getErrorMessage } from "../lib/apiErrors";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { useCustomers, useCreateCustomer } from "../hooks/useCustomers";
import type { CustomerDetailResponse, CreateCustomerRequest } from "../types/customers";

interface CustomerSearchComboboxProps {
  organizationId: string;
  selectedCustomer: CustomerDetailResponse | null;
  onSelect: (customer: CustomerDetailResponse | null) => void;
  error?: string;
  disabled?: boolean;
}

export function CustomerSearchCombobox({
  organizationId,
  selectedCustomer,
  onSelect,
  error,
  disabled = false,
}: CustomerSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  const createCustomerMutation = useCreateCustomer();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch customers based on debounced search
  const { data: customersData, isLoading } = useCustomers(
    debouncedSearch.length >= 1 || !selectedCustomer
      ? {
          searchQuery: debouncedSearch || undefined,
          organizationIds: [organizationId],
          pageSize: 10,
        }
      : undefined
  );

  const customers = customersData?.customers || [];

  const handleSelect = useCallback((customer: CustomerDetailResponse) => {
    onSelect(customer);
    setOpen(false);
    setSearchValue("");
    setShowCreateForm(false);
  }, [onSelect]);

  const handleClear = useCallback(() => {
    onSelect(null);
    setSearchValue("");
  }, [onSelect]);

  const handleShowCreateForm = useCallback(() => {
    setShowCreateForm(true);
    // Pre-populate email or name from search if it looks like an email
    if (searchValue.includes("@")) {
      setCreateFormData((prev) => ({ ...prev, email: searchValue }));
    } else if (searchValue.trim()) {
      const parts = searchValue.trim().split(" ");
      setCreateFormData((prev) => ({
        ...prev,
        firstName: parts[0] || "",
        lastName: parts.slice(1).join(" ") || "",
      }));
    }
  }, [searchValue]);

  const handleCancelCreate = useCallback(() => {
    setShowCreateForm(false);
    setCreateFormData({ firstName: "", lastName: "", email: "", phone: "" });
    setCreateErrors({});
  }, []);

  const validateCreateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!createFormData.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!createFormData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    if (!createFormData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createFormData.email)) {
      errors.email = "Invalid email format";
    }
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  }, [createFormData]);

  const handleCreateCustomer = useCallback(async () => {
    if (!validateCreateForm()) return;

    try {
      const request: CreateCustomerRequest = {
        firstName: createFormData.firstName.trim(),
        lastName: createFormData.lastName.trim(),
        email: createFormData.email.trim(),
        phone: createFormData.phone.trim() || undefined,
      };

      const newCustomer = await createCustomerMutation.mutateAsync({
        customer: request,
        organizationId,
      });

      handleSelect(newCustomer);
      handleCancelCreate();
    } catch (err) {
      console.error("Failed to create customer:", err);
      setCreateErrors({ submit: getErrorMessage(err, "Failed to create customer. Please try again.") });
    }
  }, [
    validateCreateForm,
    createFormData,
    createCustomerMutation,
    organizationId,
    handleSelect,
    handleCancelCreate,
  ]);

  // Render selected customer display
  if (selectedCustomer && !open) {
    return (
      <div className="space-y-2">
        <div
          className={cn(
            "flex items-center justify-between gap-2 rounded-md border p-3",
            error ? "border-red-500" : "border-input",
            disabled && "opacity-50"
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{selectedCustomer.fullName}</p>
              <p className="text-xs text-muted-foreground truncate">
                {selectedCustomer.email}
                {selectedCustomer.company && ` • ${selectedCustomer.company}`}
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={disabled}
            className="flex-shrink-0"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Clear selection</span>
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              error && "border-red-500",
              !selectedCustomer && "text-muted-foreground"
            )}
          >
            <span className="truncate">
              {selectedCustomer
                ? selectedCustomer.fullName
                : "Search for a customer..."}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          {showCreateForm ? (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm">Create New Customer</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelCreate}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="create-firstName" className="text-xs">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="create-firstName"
                      value={createFormData.firstName}
                      onChange={(e) =>
                        setCreateFormData((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      placeholder="John"
                      className={cn("h-8", createErrors.firstName && "border-red-500")}
                    />
                    {createErrors.firstName && (
                      <p className="text-xs text-red-600">{createErrors.firstName}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="create-lastName" className="text-xs">
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="create-lastName"
                      value={createFormData.lastName}
                      onChange={(e) =>
                        setCreateFormData((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                      placeholder="Doe"
                      className={cn("h-8", createErrors.lastName && "border-red-500")}
                    />
                    {createErrors.lastName && (
                      <p className="text-xs text-red-600">{createErrors.lastName}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="create-email" className="text-xs">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="create-email"
                    type="email"
                    value={createFormData.email}
                    onChange={(e) =>
                      setCreateFormData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder="john@example.com"
                    className={cn("h-8", createErrors.email && "border-red-500")}
                  />
                  {createErrors.email && (
                    <p className="text-xs text-red-600">{createErrors.email}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="create-phone" className="text-xs">
                    Phone Number
                  </Label>
                  <Input
                    id="create-phone"
                    type="tel"
                    value={createFormData.phone}
                    onChange={(e) =>
                      setCreateFormData((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    placeholder="(555) 123-4567"
                    className="h-8"
                  />
                </div>

                {createErrors.submit && (
                  <p className="text-xs text-red-600">{createErrors.submit}</p>
                )}

                <div className="flex flex-col gap-2 pt-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateCustomer}
                    disabled={createCustomerMutation.isPending}
                    className="w-full"
                  >
                    {createCustomerMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create & Select"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCancelCreate}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search by name or email..."
                value={searchValue}
                onValueChange={setSearchValue}
              />
              <CommandList>
                <CommandGroup>
                  <CommandItem
                    onSelect={handleShowCreateForm}
                    className="cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>Create new customer</span>
                    {searchValue && (
                      <span className="ml-1 text-muted-foreground truncate">
                        "{searchValue}"
                      </span>
                    )}
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
                {isLoading ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin mb-2" />
                    Searching...
                  </div>
                ) : customers.length === 0 && debouncedSearch.length > 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No customers found for "{debouncedSearch}"
                  </div>
                ) : customers.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Start typing to search customers
                  </div>
                ) : (
                  <CommandGroup heading="Customers">
                    {customers.map((customer) => (
                      <CommandItem
                        key={customer.id}
                        value={customer.id}
                        onSelect={() => handleSelect(customer)}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted flex-shrink-0">
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">
                              {customer.fullName}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {customer.email}
                              {customer.company && ` • ${customer.company}`}
                            </p>
                          </div>
                          {selectedCustomer?.id === customer.id && (
                            <Check className="h-4 w-4 flex-shrink-0" />
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
